import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Select,
  Spin,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { disabledDate, getFiveDaysAgo, getToday } from "../../../utils/getDays";
import { http } from "../../../services/http";
import { useSearchParams } from "react-router-dom";
import { useRouterPush } from "../../../hooks/use-router-push";
import { useLocationParams } from "../../../hooks/use-location-params";
import moment from "moment";
import debounce from "lodash.debounce";
import dayjs from "dayjs";
import {
  formatMoney,
  handleInputChangeMoney,
  removeSpaces,
} from "../../../utils/formatMoney";

const CreatePaymentCustomer = ({ refetchTable }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { push } = useRouterPush();
  const [loading, setLoading] = useState(false);
  const [rateLoading, setRateLoading] = useState(false);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersData, setPartnersData] = useState([]);
  const { query } = useLocationParams();
  const [skip, setSkip] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [docRate, setDocRate] = useState(0);
  const [docRateApi, setDocRateApi] = useState(0);

  useEffect(() => {
    if (!query.add || !query.edit) {
      setPartnersData([]);
      setSkip(0);
      setHasMoreData(true);
    }
  }, [query.add, query.edit]);

  const fetchGroup = async (newSkip, searchValue = "") => {
    if (partnersLoading || !hasMoreData) return;
    try {
      setPartnersLoading(true);
      const queryParam = searchValue ? `CardName=${searchValue}&` : ""; // Agar searchValue bo'sh bo'lsa, CardName parametresi yuborilmaydi
      const { data } = await http.get(
        `api/cashier/business-partners?${queryParam}skip=${newSkip || 0}&cardType=cCustomer&CardName=${searchValue}`,
      );
      if (data.data.length < 20) {
        setHasMoreData(false); // Stop further loading if less than 20 items are returned
      }
      const mappedItems = data.data.map((item) => ({
        label: item.cardName,
        value: item.cardCode,
      }));

      setPartnersData((prevData) => [...prevData, ...mappedItems]);
    } catch (err) {
      console.log(err);
    } finally {
      setPartnersLoading(false);
    }
  };

  const handleSearch = useCallback(
    debounce((value) => {
      setSearchValue(value);
      setHasMoreData(true); // Filterlashda yuklashni yana yoqish
      setPartnersData([]);
      fetchGroup(0, value); // Filter so'rovini yuborish
    }, 300),
    [], // Debounce intervali (300ms)
  );

  const handleScroll = (e) => {
    const { target } = e;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
      setSkip((prevSkip) => {
        const newSkip = prevSkip + 1;
        fetchGroup(newSkip, searchValue); // Scroll uchun oxirgi searchValue yuboriladi
        return newSkip;
      });
    }
  };

  const getCurrencyRate = async () => {
    try {
      setRateLoading(true);
      const { data } = await http.get(
        `api/cashier/currency-rate?Currency=UZS&Date=${form.getFieldValue("docDate")}`,
      );
      setDocRateApi(Number(data.data));
      setDocRate(Number(data.data));
      form.setFieldsValue({ docRate: Number(data.data) });
    } catch (err) {
      console.log(err);
    } finally {
      setRateLoading(false);
    }
  };

  useEffect(() => {
    if (query.add) {
      fetchGroup(0); // SearchValue bo'lmagan holda chaqirish
      getCurrencyRate();
      form.setFieldsValue({
        docDate: dayjs(),
      });
    }
  }, [query.add]);

  const postData = async (values) => {
    try {
      setLoading(true);
      await http.post("api/cashier/incoming-payment", {
        ...values,
        docDate: dayjs(values.docDate).format("YYYY-MM-DD"),
        docType: "rCustomer",
        cashSum: removeSpaces(values.cashSum),
        docRate: removeSpaces(docRate),
      });
      refetchTable();
      message.success({ content: t("sucCreated") });
      onClose();
    } catch (err) {
      message.error({ content: JSON.stringify(err) });
    } finally {
      setLoading(false);
    }
  };

  const getSingleData = async () => {
    try {
      setLoading(true);
      const data = await http.get(`api/cashier/incoming-payment/${query.id}`);
      await fetchGroup(0, data?.data?.data.cardName);
      form.setFieldsValue({
        ...data?.data?.data,
        docDate: dayjs(data?.data?.data.docDate),
        cashSum:
          data?.data?.data.docCurrency === "USD"
            ? data?.data?.data.cashSum
            : data?.data?.data.cashSumFC,
      });
    } catch (err) {
      message.error({ content: JSON.stringify(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (query.edit && query.id) {
      getSingleData();
    }
  }, [query.id, query.edit]);

  const onClose = () => {
    push(
      { query: { add: undefined, edit: undefined, id: undefined } },
      { update: true },
    );
    form.resetFields();
  };

  return (
    <Modal
      open={!!query.add || !!query.edit}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseOutlined />}
      width="80%"
      style={{ height: "80%" }}
    >
      <Spin spinning={loading || rateLoading}>
        <div className="w-full sm:px-14">
          <Form
            form={form}
            layout={"vertical"}
            onFinish={postData}
            initialValues={{
              docDate: dayjs(),
            }}
          >
            <div className="mt-10 grid gap-3 sm:grid-cols-1 md:grid-cols-3">
              <Form.Item name="docDate" label={t("date")}>
                <DatePicker
                  format="DD.MM.YYYY"
                  onChange={getCurrencyRate}
                  disabledDate={disabledDate}
                  className="h-12 w-full font-bold"
                />
              </Form.Item>
              <Form.Item name="cardCode" label={t("mijoz")}>
                <Select
                  showSearch
                  className="h-12 font-normal"
                  options={[
                    ...partnersData,
                    ...(partnersLoading
                      ? [
                          {
                            label: <Spin size="small" tip="Loading..." />,
                            value: "loading",
                          },
                        ]
                      : []),
                  ]}
                  loading={partnersLoading}
                  virtual={true}
                  onSearch={handleSearch} // Yozilgan so'zlar uchun
                  onPopupScroll={handleScroll} // Scroll uchun
                  notFoundContent={
                    partnersLoading ? <Spin size="small" /> : null
                  } // Yuklanayotgan spinner
                  placeholder={t("search")}
                  filterOption={false} // Ant Designning ichki filtratsiyasini o'chirish
                />
              </Form.Item>
              <Form.Item
                name="docRate"
                label={t("Dollar kursi")}
                rules={[
                  {
                    required: true,
                    message: t("required"),
                  },
                  {
                    validator: (_, value) => {
                      if (
                        !value ||
                        (value >= docRateApi - 50 && value <= docRateApi + 50)
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          t("range_error", {
                            min: docRateApi - 50,
                            max: docRateApi + 50,
                          }),
                        ),
                      );
                    },
                  },
                ]}
              >
                <Input
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  value={docRate}
                  onChange={(e) => setDocRate(e.target.value)}
                  className="h-12"
                />
              </Form.Item>
              <Form.Item name="cashSum" label={t("Cash sum")}>
                <Input
                  className="h-12"
                  onChange={(e) => handleInputChangeMoney(e, form, "cashSum")}
                />
              </Form.Item>
              <Form.Item name="docCurrency" label={t("currency")}>
                <Select
                  showSearch
                  className="h-12"
                  options={[
                    { label: t("USD"), value: "USD" },
                    { label: t("UZS"), value: "UZS" },
                  ]}
                />
              </Form.Item>
              <Form.Item name="remarks" label={t("comments")}>
                <Input.TextArea rows={4} />
              </Form.Item>
            </div>
            <div className="mt-24 flex justify-center">
              {!query.edit && (
                <Button
                  type={"primary"}
                  htmlType={"submit"}
                  className="h-12 w-full rounded-3xl bg-[#0A4D68] text-white sm:w-[350px]"
                  disabled={
                    !(
                      docRate >= docRateApi - 50 && docRate <= docRateApi + 50
                    ) || loading
                  }
                  loading={loading}
                >
                  {t("create")}
                </Button>
              )}
            </div>
          </Form>
        </div>
      </Spin>
    </Modal>
  );
};

export default CreatePaymentCustomer;
